// Search, after SSearch (desktop) and SPhoneSearch (phone): everything you can open (activities, tracks, minutes,
// emails, files, people), by kind and tag, title matches first, then the most recent. The words live in the address
// (?q=&kind=&tags=), so Back and a shared link come back to the same results. The assistant answers the same words
// only when asked (Enter, or Ask the assistant), as a conversation that "Continue in the assistant" opens.
import { ToLink } from '../ui/ActLink';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { SEARCH_KINDS, type SearchKind, type SearchResult } from '@dpw/shared';
import { api } from '../api';
import { usePhone } from '../lib/media';
import { useSpaces } from '../lib/queries';
import { listItem, spring } from '../lib/motion';
import { ErrorNote } from '../ui/bits';
import { Orb, SkelList } from '../ui/motion';
import { IconSearch, IconX } from '../ui/icons';
import { P, Section } from '../phone/kit';
import { KINDS, abbrOf, phoneGroups, recentSearches, rememberSearch, tagsText, whenText } from '../search/model';
import { PhoneSaveRow, PhoneSavedList, SaveSearchButton, SavedPanel } from '../search/Saved';
import { useAssistantPanel } from '../assistant/Panel';

const isKind = (k: string | null): k is SearchKind => !!k && (SEARCH_KINDS as readonly string[]).includes(k);

/** The words, kind and tags in the address, and the results for them. */
function useSearchState() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const kindParam = params.get('kind');
  const kind = isKind(kindParam) ? kindParam : undefined;
  const tags = (params.get('tags') ?? '').split(',').filter(Boolean);
  const [text, setText] = useState(q);
  // Typing updates the address after a short pause (replace: Back leaves Search, not each letter).
  useEffect(() => {
    const t = setTimeout(() => {
      if (text.trim() === q) return;
      const next = new URLSearchParams(params);
      if (text.trim()) next.set('q', text.trim()); else next.delete('q');
      setParams(next, { replace: true });
    }, 220);
    return () => clearTimeout(t);
  }, [text, q, params, setParams]);
  // Back and forward, or a link with ?q=, bring their words into the box.
  useEffect(() => { setText(t => (t.trim() === q ? t : q)); }, [q]);
  const set = (k: 'kind' | 'tags', v: string | null) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };
  const results = useQuery({
    queryKey: ['search-all', q, kind ?? 'all', tags.join(',')],
    queryFn: () => api.search.all(q, { kind, tags }),
    enabled: q.length >= 2, placeholderData: keepPreviousData, staleTime: 15_000,
  });
  return {
    q, kind, tags, text, setText, results,
    setKind: (k: SearchKind | 'all') => set('kind', k === 'all' ? null : k),
    toggleTag: (t: string) => set('tags', (tags.includes(t) ? tags.filter(x => x !== t) : [...tags, t]).join(',') || null),
    clearTags: () => set('tags', null),
  };
}

export function SearchPage() {
  return usePhone() ? <PhoneSearch /> : <DesktopSearch />;
}

function DesktopSearch() {
  const s = useSearchState();
  const input = useRef<HTMLInputElement>(null);
  // Asking opens the assistant panel (one AI surface everywhere), with the words sent.
  const panel = useAssistantPanel();
  useEffect(() => { input.current?.focus(); }, []);
  const words = s.text.trim();
  const submit = (e: FormEvent) => { e.preventDefault(); if (words.length >= 2) { rememberSearch(words); panel.open({ prompt: words, send: true, fresh: true }); } };
  const data = s.results.data;
  const items = s.q.length >= 2 ? data?.items ?? [] : [];
  const total = data ? Object.values(data.counts).reduce((a, b) => a + b, 0) : 0;
  const now = { q: s.q, kind: s.kind, tags: s.tags };
  return (
    <div className="srch col" style={{ gap: 16 }}>
      <div className="hdr"><div className="htl"><h1>Search</h1><span className="xs mut">Activities, tracks, minutes, emails, files, people, insights and the Knowledge base</span></div></div>
      <form className="bigsearch" onSubmit={submit} role="search">
        <IconSearch />
        <input ref={input} value={s.text} onChange={e => s.setText(e.target.value)} placeholder="Search everything, or type a question" aria-label="Search" autoComplete="off" />
        {s.text && <button type="button" className="ib xq" onClick={() => { s.setText(''); input.current?.focus(); }} aria-label="Clear search"><IconX /></button>}
        <button className="bt acc" disabled={words.length < 2}>Ask the assistant instead</button>
      </form>
      <div className="rowc">
        <div className="seg2" role="group" aria-label="Kind">
          {KINDS.map(([k, label]) => {
            const on = (s.kind ?? 'all') === k;
            const n = !data ? '' : k === 'all' ? total : data.counts[k];
            return <button key={k} type="button" className={on ? 'on' : ''} aria-pressed={on} onClick={() => s.setKind(k)}>{label} <span className="mono xs">{n}</span></button>;
          })}
        </div>
        <span style={{ flex: 1 }} />
        <SaveSearchButton now={now} />
      </div>
      {(data?.tags.length ?? 0) > 0 && (
        <div className="rowc" role="group" aria-label="Tags"><span className="k" style={{ marginRight: 4 }}>Tags</span>
          {data!.tags.map(t => <button key={t.tag} type="button" className={`tagb${s.tags.includes(t.tag) ? ' on' : ''}`} aria-pressed={s.tags.includes(t.tag)} onClick={() => s.toggleTag(t.tag)}>#{t.tag}</button>)}
          {s.tags.length > 0 && <button type="button" className="bt ghost" onClick={s.clearTags}>Clear</button>}
        </div>
      )}
      <div className="colsx">
        <div className="l">
          <div className="panel srres" role="region" aria-label="Results">
            {s.q.length < 2 ? <div className="sm mut emptyr">Type two letters or more. Search looks in titles, places, tags, minutes, emails and the text of files.</div> : <>
              <div className="rowc btwc srhead"><span className="k">{s.results.isFetching && !data ? 'Searching' : `${items.length} ${items.length === 1 ? 'result' : 'results'}`}</span><span className="xs mut">Sorted by relevance, then recency</span></div>
              {s.results.isLoading && <SkelList rows={6} variant="conv" />}
              {s.results.error ? <ErrorNote error={s.results.error} /> : null}
              <AnimatePresence initial={false}>
                {items.map(r => <ResultRow key={`${r.kind}:${r.id}`} r={r} />)}
              </AnimatePresence>
              {data && !items.length && <div className="sm mut emptyr">Nothing matches. Try fewer tags, or ask the assistant.</div>}
            </>}
          </div>
        </div>
        <div className="r">
          <section className="mini ai" aria-label="Ask the assistant"><span className="k">Ask the assistant</span>
            <div className="sm" style={{ lineHeight: 1.55 }}>Press Enter, or Ask the assistant, and it answers in its panel from activities, minutes, emails and the Knowledge base, with links to each source.</div></section>
          <SavedPanel now={now} />
        </div>
      </div>
    </div>
  );
}

function ResultRow({ r }: { r: SearchResult }) {
  return (
    <motion.div variants={listItem} initial="initial" animate="animate" exit="exit" layout="position" transition={spring.gentle}>
      <ToLink className="res" to={r.href} data-kind={r.kind}>
        <span className="ico" aria-hidden="true">{abbrOf(r)}</span>
        <div className="rt"><div className="sm tt">{r.title}</div><div className="xs mut">{r.where}{r.tags.length > 0 && <> · <span className="mono">{tagsText(r.tags)}</span></>}</div></div>
        <span className="xs mut mono when">{whenText(r)}</span>
      </ToLink>
    </motion.div>
  );
}

function PhoneSearch() {
  const s = useSearchState();
  const panel = useAssistantPanel();
  const nav = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [recent, setRecent] = useState(recentSearches);
  const first = useSpaces().data?.[0];
  useEffect(() => { input.current?.focus(); }, []);
  // A search that found something is worth keeping for next time.
  useEffect(() => { if (s.q.length >= 2 && s.results.data?.items.length) { rememberSearch(s.q); setRecent(recentSearches()); } }, [s.q, s.results.data]);
  const items = s.q.length >= 2 ? s.results.data?.items ?? [] : [];
  const groups = useMemo(() => phoneGroups(items), [items]);
  const empty = !s.text.trim();
  return (
    <div className="ph-page phs">
      <div className="ph-shead">
        <label className="ph-sbox"><P.search /><input ref={input} value={s.text} onChange={e => s.setText(e.target.value)} placeholder="Search everything" aria-label="Search" autoComplete="off" enterKeyHint="search" /></label>
        <button type="button" className="ph-cancel" onClick={() => nav(-1)}>Cancel</button>
      </div>
      {empty && <>
        <PhoneSavedList />
        {recent.length > 0 && <>
          <Section title="Recent" />
          <div className="ph-grp flat" role="group" aria-label="Recent">{recent.map(t => (
            <button key={t} type="button" className="ph-row" onClick={() => s.setText(t)}><span className="ph-ic"><P.search /></span><span className="ph-rt"><div className="t">{t}</div></span></button>
          ))}</div>
        </>}
        <Section title="Jump to" />
        <div className="ph-chips" role="group" aria-label="Jump to">
          {first ? <Link className="ph-chip" to={`/spaces/${first.id}`}>{first.name}</Link> : <Link className="ph-chip" to="/spaces">Spaces</Link>}
          <Link className="ph-chip" to="/calendar">Calendar</Link><Link className="ph-chip" to="/folders">Knowledge base</Link>
          <Link className="ph-chip" to="/knowledge">Research Center</Link><Link className="ph-chip" to="/reminders">Reminders</Link>
        </div>
      </>}
      {!empty && <>
        <button type="button" className="ph-askrow" onClick={() => panel.open({ prompt: s.text.trim(), send: true, fresh: true })}><span className="ph-aio"><Orb size={22} /></span>
          <span className="ph-rt"><div className="t">Ask the assistant about "{s.text.trim()}"</div><div className="s">Answers with sources</div></span><P.chev /></button>
        <PhoneSaveRow now={{ q: s.q, kind: s.kind, tags: s.tags }} />
        {s.q.length >= 2 && s.results.isLoading && <SkelList rows={4} variant="conv" />}
        {groups.map(g => (
          <div key={g.group}>
            <Section title={g.group} count={g.items.length} />
            <div className="ph-grp" role="group" aria-label={g.group}>{g.items.map(r => (
              <ToLink key={`${r.kind}:${r.id}`} className="ph-row" to={r.href}><span className="ph-rt"><div className="t">{r.title}</div>
                <div className="s">{[r.where, whenText(r)].filter(Boolean).join(' · ')}</div></span><P.chev /></ToLink>
            ))}</div>
          </div>
        ))}
        {s.q.length >= 2 && s.results.data && !items.length && <div className="f13 ph-nores">No matches. Ask the assistant instead.</div>}
      </>}
    </div>
  );
}
